/**
 * clipTranscriptProxies.ts
 *
 * Creates proxy objects that make clip-specific editor data look like
 * the global RootStore stores. This allows the full Transcript component
 * to render clip transcript data without any modifications to Transcript.tsx
 * or its sub-components.
 *
 * Strategy: Wrap the real RootStore with a Proxy that overrides
 * transcriptionStore, editorStore, and karaokeStore to read from
 * the ClipPreviewEditorStore instead of global state.
 */

import { LayoutEngine } from '@src/editor/models/layout/LayoutEngine';
import {
  addSpaceToEndOfTranscriptElements,
  fillInWhitespacesInTranscriptElements,
} from '@src/editor/services/punctuation';
import { resolveSceneDetectionsForEdit } from '@src/services/framing/resolveSceneDetectionsForEdit';
import {
  type ISceneFramingService,
  SmartFramingService,
} from '@src/services/framing/SmartFramingService';
import { SpeakerAwareSceneFramer } from '@src/services/framing/SpeakerAwareSceneFramer';
import { SceneService } from '@src/services/SceneService';
import { TranscriptionStatus } from '@src/types.ts/video';
import { LogManager } from '@src/utility/Logger';
import { extractTranscriptionText } from '@src/utility/transcriptFormatUtils';
import { observable, untracked } from 'mobx';

import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type { IObservableValue } from 'mobx';

/**
 * PLAT-11150 — the single read point for the clip's transcript, frozen while
 * the Transcript tab is hidden.
 *
 * `refreshTranscriptElements` allocates a new array on *every* timeline change,
 * so a hidden transcript re-rendered its whole word list on every reel edit:
 * `TranscriptionText` is memoized on array identity, and a fresh array fails
 * that check. Holding the last array while hidden makes the memo bail out
 * instead — the pane keeps its DOM, its scroll position and its effects, and
 * simply shows what it last showed until the tab is opened again.
 *
 * Two details carry the correctness:
 *
 * 1. While frozen this must **not touch** `editorStore.transcriptElements`.
 *    Reading a MobX observable is what subscribes the surrounding `observer`
 *    components to it, so a read here — even one whose result is discarded —
 *    is what would make a hidden transcript re-render at all.
 * 2. `isActive` is read on **every** call, unconditionally. It is therefore the
 *    only observable a frozen transcript is subscribed to, which is what makes
 *    flipping it back on invalidate those observers and pull the live array
 *    through. A plain boolean or ref would leave the surface stale forever:
 *    `observer` also memoizes on props, so nothing else would tell it to
 *    re-render.
 *
 * Shared by the transcription and editor proxies so the two can never disagree
 * about which array the surface is showing.
 */
function createFrozenElementsReader(
  editorStore: ClipPreviewEditorStore,
  isActive: IObservableValue<boolean>,
): () => readonly any[] {
  /** Last array handed out while the surface was active — the frozen view. */
  let frozen: readonly any[] | null = null;

  return () => {
    const active = isActive.get();
    if (!active) {
      if (frozen) return frozen;
      // First read while already hidden — the modal opened on another tab, so
      // there is no earlier array to hold. Snapshot one `untracked`, because
      // the invariant worth keeping absolute is that a hidden surface is never
      // subscribed to the transcript: a plain read here would subscribe it and
      // it would then re-render on the next reel edit, which is the whole cost
      // this exists to remove.
      frozen = untracked(() => editorStore.transcriptElements);
      return frozen;
    }
    const live = editorStore.transcriptElements;
    frozen = live;
    return live;
  };
}

const logger = LogManager.getLogger('hooks:clipTranscriptProxies');

/**
 * Create a TranscriptionStore-like proxy that reads from the clip's editor.
 *
 * The clip's transcript is derived from the story's transcript, so its source
 * language is the story-level language. Delegate `language` to the real
 * story-level transcription store (reactively, via getters) instead of
 * hardcoding 'en' — otherwise the clip-preview Translate modal mislabels the
 * source language for non-English stories (PLAT-10421).
 */
function createTranscriptionProxy(
  editorStore: ClipPreviewEditorStore,
  realRootStore: RootStore,
  readElements: () => readonly any[],
) {
  // Cache for patched transcript elements (avoids new array ref on every call)
  let _cachedSource: readonly any[] | null = null;
  let _cachedPatched: any[] | null = null;
  return {
    get transcriptionElements() {
      return readElements();
    },
    get transcriptionLoadingStatus() {
      return editorStore.isReady
        ? TranscriptionStatus.Loaded
        : TranscriptionStatus.Loading;
    },
    get isTranscriptionReady() {
      return editorStore.isReady && readElements().length > 0;
    },
    get isTranscriptionLoading() {
      return !editorStore.isReady;
    },
    get isTranscriptEditorCompatible() {
      return readElements().length > 0;
    },
    hasUnsavedChanges: false,
    transcriptionChanges: [],
    get language() {
      return realRootStore.transcriptionStore.language;
    },
    // Same delegation rationale as `language`: caption-font script resolution
    // (PLAT-11407) reads `rawLanguage` to resolve the 'original' sentinel.
    get rawLanguage() {
      return realRootStore.transcriptionStore.rawLanguage;
    },
    currentTranscriptVersion: 'latest',
    transcriptionUrl: 'clip-preview', // Must be truthy to bypass "regenerating" guard in Transcript.tsx
    getOriginalTranscription() {
      const language = realRootStore.transcriptionStore.language;
      const original = editorStore.editor.getOriginalTranscript();
      if (original) {
        return { elements: original, language };
      }
      // Fallback: use the observable transcript elements so MobX can track this
      // access and re-render when transcript data arrives via late-loading.
      const current = readElements();
      if (current.length > 0) {
        return { elements: current, language };
      }
      return null;
    },
    /**
     * The real store exposes this as a field, and some callers read it
     * directly rather than through `getOriginalTranscription()`. Without this
     * getter those reads land on `undefined` and silently degrade — the
     * speaker-identification modal lost its whole transcript that way, which
     * left it unable to sample any video frames (PLAT-11173).
     */
    get originalTranscription() {
      return this.getOriginalTranscription();
    },
    /**
     * Read-only: this transcript is derived from the scoped editor, so there
     * is nowhere to write it back to. Assigning story-level data here would
     * either be lost or overwrite the story transcript with clip-derived
     * elements. `undoSectionOverride` assigns to this field — under a scoped
     * proxy its restore does not persist, as was already the case when the
     * getter above did not exist and its guard read `undefined`.
     */
    set originalTranscription(_value: unknown) {
      logger.warn(
        'originalTranscription is read-only on a scoped proxy; write ignored',
      );
    },
    getFinalTranscriptionElements() {
      const elements = readElements();
      // Return cached result if source hasn't changed (stable ref for MobX)
      if (elements === _cachedSource && _cachedPatched) return _cachedPatched;
      _cachedSource = elements;

      // Strip leading whitespace element so the first word starts flush
      // with the left edge (preserves indices by blanking value, not removing).
      if (
        elements.length > 0 &&
        elements[0].type !== 'text' &&
        elements[0].value?.trim() === ''
      ) {
        const patched = elements.slice();
        patched[0] = { ...patched[0], value: '' };
        _cachedPatched = patched;
      } else {
        _cachedPatched = elements as any[];
      }
      return _cachedPatched;
    },
    getFinalTranscriptionSlice(startIdx: number, endIdx: number) {
      return readElements().slice(startIdx, endIdx);
    },
    getTranscriptionText() {
      return extractTranscriptionText(readElements() as any[]);
    },
    getTranscriptionChangesForSave() {
      return [];
    },
    getTextFromRange(startIndex: number, endIndex: number) {
      const elements = readElements();
      if (!elements.length || startIndex > endIndex) return '';
      return extractTranscriptionText(
        elements.slice(startIndex, endIndex + 1) as any[],
      );
    },
  };
}

/**
 * Create an EditorStore-like proxy that wraps ClipPreviewEditorStore.
 */
function createEditorProxy(
  editorStore: ClipPreviewEditorStore,
  readElements: () => readonly any[],
  getSpeakerMap: () => Record<string, string[]> | null,
) {
  let _framingService: ISceneFramingService | null = null;

  return {
    get editor() {
      return editorStore.editor;
    },
    get timeline() {
      return editorStore.timeline;
    },
    get isReady() {
      return editorStore.isReady;
    },
    get selectedElementIds() {
      return editorStore.selectedElementIds;
    },
    get transcriptElements() {
      // Frozen with the transcription proxy (PLAT-11150): a live read here
      // would re-subscribe the very observers the freeze exists to detach.
      return readElements();
    },
    get hasKaraokeTrack() {
      return editorStore.editor.getKaraokeLines().length > 0;
    },
    get framingService(): ISceneFramingService {
      if (!_framingService) {
        _framingService = new SpeakerAwareSceneFramer(
          new SmartFramingService(
            editorStore.editor,
            new SceneService(),
            new LayoutEngine(),
          ),
          getSpeakerMap,
        );
      }
      return _framingService;
    },
    sceneDetectionsForEdit(
      shots: ReadonlyArray<SceneDetection> | null | undefined,
    ): SceneDetection[] | undefined {
      const { editor } = editorStore;
      return resolveSceneDetectionsForEdit({
        shots,
        transcript: editor.getOriginalTranscript(),
        aspectRatio: editor.timeline.width / editor.timeline.height,
        speakerMap: getSpeakerMap(),
      });
    },
    selectElement(id: string) {
      editorStore.selectElement(id);
    },
    // Stubs for properties that Transcript doesn't actively use but may read
    activeTranscript: [],
    transcriptSegmentArchitectureNotSupported: false,
    transcriptSegmentArchitectureMigrationLog: null,
  };
}

/**
 * Create a KaraokeStore-like proxy that reads karaoke data from the clip's editor.
 * Uses the observable `karaokeLines` field from ClipPreviewEditorStore so that
 * MobX observers (Transcript, useDisplayTranscriptElements) re-render after
 * karaoke commands (add/remove break, hide/show, etc.).
 */
function createKaraokeProxy(editorStore: ClipPreviewEditorStore) {
  return {
    get hasKaraokeLines() {
      return editorStore.karaokeLines.length > 0;
    },
    get karaokeLines() {
      return editorStore.karaokeLines;
    },
    // PLAT-11905 — the source-timed caption set, shown or parked, for the
    // out-of-date notice.
    get sourceCaptionLines() {
      return editorStore.karaokeStore.sourceCaptionLines;
    },
    // PLAT-11905 — which caption set is shown, for the caption style lookup
    // on caption click.
    get liveCaptionKind() {
      return editorStore.karaokeStore.liveCaptionKind;
    },
    get karaokeConfigs() {
      return editorStore.editor.getKaraokeConfigs();
    },
    karaokeLanguage: 'original' as const,
    isKaraokeInitialized: true,
    isKaraokeLoading: false,
    isReloading: false,
    previousKaraokeLines: null,
    getKaraokeLinesFormatted() {
      // Apply the same whitespace post-processing as KaraokeStore.getKaraokeLinesFormatted()
      return editorStore.karaokeLines.map(line => ({
        ...line,
        transcriptTokens: addSpaceToEndOfTranscriptElements(
          fillInWhitespacesInTranscriptElements(
            line.transcriptTokens.filter(token => token.value !== '\n'),
          ),
        ),
      }));
    },
    // PLAT-10788 — delegate to the clip's owned KaraokeStore so the clip's
    // caption toggle can read the saved active-language variant
    // (`CaptionsLanguageHeaderToggle.savedCaptionsLang`) and per-language state.
    get loadedKaraokeData() {
      return editorStore.karaokeStore.loadedKaraokeData;
    },
    get activeLanguage() {
      return editorStore.karaokeStore.activeLanguage;
    },
    getKaraokeData() {
      return editorStore.karaokeStore.getKaraokeData();
    },
    getKaraokeDataForLanguage(lang: string) {
      return editorStore.karaokeStore.getKaraokeDataForLanguage(lang);
    },
    setActiveLanguage(lang: string) {
      editorStore.karaokeStore.setActiveLanguage(lang);
    },
    getKaraokeLineById(lineId: string) {
      return editorStore.karaokeLines.find(l => l.id === lineId);
    },
    get isKaraokeReady() {
      return editorStore.karaokeLines.length > 0;
    },
    get lineCount() {
      return editorStore.karaokeLines.length;
    },
    // PLAT-11689 — the Cap toggle's state, from the clip's own config. Without
    // it the clip's Cap read `undefined`: always pressed, "Show" unreachable.
    get captionsHidden() {
      return editorStore.karaokeStore.captionsHidden;
    },
    get defaultKaraokeConfig() {
      return editorStore.karaokeStore.defaultKaraokeConfig;
    },
    // A surface's explicit repaint after a karaoke command lands on the clip's
    // own store (the editor observer does the same on TIMELINE_CHANGED).
    syncFromEditor() {
      editorStore.refreshKaraokeState();
    },
  };
}

/**
 * Create a RootStore proxy that overrides transcriptionStore, editorStore,
 * and karaokeStore with clip-specific proxies while delegating everything
 * else to the real RootStore.
 */
export function createClipRootProxy(
  realRootStore: RootStore,
  editorStore: ClipPreviewEditorStore,
  videoSourceStore?: VideoSourceStore,
  /**
   * PLAT-11150 — whether the surface reading through this proxy is on screen.
   * While it is not, the transcript is served frozen; see
   * {@link createFrozenElementsReader}. Defaults to always-active, so hosts
   * that never hide their transcript need not pass anything.
   */
  isActive: IObservableValue<boolean> = observable.box(true),
): RootStore {
  const readElements = createFrozenElementsReader(editorStore, isActive);
  const transcriptionProxy = createTranscriptionProxy(
    editorStore,
    realRootStore,
    readElements,
  );
  const editorProxy = createEditorProxy(
    editorStore,
    readElements,
    () => realRootStore.speakerFaceMapForFraming,
  );
  const karaokeProxy = createKaraokeProxy(editorStore);
  const videoSourceStoreProxy =
    videoSourceStore ?? realRootStore.videoSourceStore;

  return new Proxy(realRootStore, {
    get(target, prop, receiver) {
      if (prop === 'transcriptionStore') return transcriptionProxy;
      if (prop === 'editorStore') return editorProxy;
      if (prop === 'karaokeStore') return karaokeProxy;
      if (prop === 'videoSourceStore') return videoSourceStoreProxy;
      return Reflect.get(target, prop, receiver);
    },
  });
}
